// src/webdavService.js
import axios from 'axios';
import { getRuntimeConfig } from '../config';
const BASE_URL = getRuntimeConfig().WEBDAV_BASE_URL;

function getConfiguredHostnames() {
  const { API_BASE_URL, WEBDAV_BASE_URL } = getRuntimeConfig();
  const hosts = new Set();
  try {
    if (API_BASE_URL) hosts.add(new URL(API_BASE_URL).hostname);
  } catch {}
  try {
    if (WEBDAV_BASE_URL) hosts.add(new URL(WEBDAV_BASE_URL).hostname);
  } catch {}
  return hosts;
}

class WebDAVService {
constructor() {
  this.thumbnailCachePrefix = 'thumbnail_cache_';
  this.thumbnailCacheTTL = 1 * 60 * 60 * 1000; // 1 hour in milliseconds
  this.listingCache = new Map(); // In-memory cache: Map<path, { timestamp: number, data: any }>
  this.listingCacheTTL = 2 * 60 * 1000; // 2 minutes in milliseconds
  this.password = '';
  this.username = '';
  this.authInitialized = false;
  this.defaultBaseUrl = BASE_URL; // Store the default
  this.currentBaseUrl = BASE_URL; // Track the current one
  
  // Add a way to track the last authenticated user
  this.lastAuthenticatedUser = null;
  
  // Initialize username and password from localStorage immediately
  this.initAuth();
}
// Method to get a thumbnail from cache or fetch and cache it
async getOrFetchThumbnail(thumbnailPath, customBaseUrl = null) {
  // ...
}

// Method to clear the thumbnail cache (optional, but good for maintenance)
clearThumbnailCache() {
  // ...
}

// Add this new function to better handle credentials storage
storeCredentials(username, password) {
  if (username && password) {
    this.username = username;
    this.password = password;
    this.authInitialized = true;
    this.lastAuthenticatedUser = username;
    
    // Plain WebDAV passwords are no longer stored client-side.
    return true;
  }
  return false;
}

// Set a custom base URL for a specific device
setCustomBaseUrl(url) {
  if (url && url.startsWith('http')) {
    this.currentBaseUrl = url;
    console.log(`Using custom base URL: ${url}`);
  }
}

// Reset to the default base URL
resetBaseUrl() {
  this.currentBaseUrl = this.defaultBaseUrl;
  console.log(`Reset to default base URL: ${this.currentBaseUrl}`);
}

// Get the current base URL (either custom or default)
getBaseUrl() {
  return this.currentBaseUrl;
}

initAuth() {
  // First try to get user info
  const userString = localStorage.getItem('user');
  let currentUser = null;
  
  if (userString) {
    try {
      const user = JSON.parse(userString);
      this.username = user.username;
      currentUser = user.username;
    } catch (error) {
      console.error('Error parsing user from localStorage:', error);
    }
  }
  
  // Check if user has changed
  if (this.lastAuthenticatedUser && this.lastAuthenticatedUser !== currentUser) {
    console.log(`User changed from ${this.lastAuthenticatedUser} to ${currentUser}, clearing WebDAV credentials`);
    // Clear credentials when user changes
    this.password = '';
    this.authInitialized = false;
    localStorage.removeItem('webdavPassword');
    localStorage.removeItem('webdavCredentials');
  }
  
  // Update last authenticated user
  this.lastAuthenticatedUser = currentUser;
  
  // WebDAV uses Bearer token auth now; no localStorage credentials.
  
  // Credentials are derived from the session token, nothing to fetch.
  this.authInitialized = true;
}

// Load credentials from localStorage (as a fallback)
loadCredentialsFromStorage() {
  const userString = localStorage.getItem('user');
  let currentUser = null;
  
  if (userString) {
    try {
      const user = JSON.parse(userString);
      currentUser = user.username;
    } catch (error) {
      console.error('Error parsing user from localStorage:', error);
      return false;
    }
  }
  
  // ...
}

// Credentials are no longer fetched from the server.
async fetchCredentials() {
  return false;
}

// Get authentication header
getAuthHeader() {
  const token = localStorage.getItem('token');
  if (token) {
    return `Bearer ${token}`;
  }

  return '';
}

// Modified promptForPassword - no longer shows a prompt
async promptForPassword() {
  // Get the current user
  const userString = localStorage.getItem('user');
  let currentUser = null;

  if (userString) {
    try {
      const user = JSON.parse(userString);
      currentUser = user.username;
      // Update our username to match
      this.username = currentUser;
    } catch (e) {
      console.error('Error parsing user for password prompt:', e);
    }
  }
  if (currentUser) {
    this.username = currentUser;
  }

  const token = localStorage.getItem('token');
  return !!token;
}
  
  // Initialize auth and credentials if needed
  async ensureAuthenticated() {
    // Get the current user
    const userString = localStorage.getItem('user');
    let currentUser = null;
    

    if (userString) {
     try {
        const user = JSON.parse(userString);
        currentUser = user.username;
      } catch (e) {
        console.error('Error parsing user for auth check:', e);
      }
    }

    if (currentUser && this.lastAuthenticatedUser && this.lastAuthenticatedUser !== currentUser) {
      console.log(`User changed from ${this.lastAuthenticatedUser} to ${currentUser}, reinitializing WebDAV credentials`);
      this.username = currentUser;
      this.password = '';
      this.authInitialized = false;
      this.clearCredentials(); // Clear old credentials
    }

    if (currentUser) {
      this.username = currentUser;
    }

    const token = localStorage.getItem('token');
    return !!token;
  }
  
// Optimized directory listing with better caching and error handling
  async getDirectoryListing(webdavPath, customBaseUrl = null, forceRefresh = false) {
    const cacheKey = webdavPath + (customBaseUrl || this.getBaseUrl());

    // Check cache first (unless force refresh)
    if (!forceRefresh) {
      const cachedEntry = this.listingCache.get(cacheKey);
      if (cachedEntry && (Date.now() - cachedEntry.timestamp < this.listingCacheTTL)) {
        console.log(`📁 Serving directory listing from cache: ${webdavPath}`);
        return cachedEntry.data;
      }
    }

    // Store original base URL for cleanup
    const originalBaseUrl = this.currentBaseUrl;
    if (customBaseUrl) {
      this.setCustomBaseUrl(customBaseUrl);
    }

    // Ensure authentication with timeout
    try {
      const authPromise = this.ensureAuthenticated();
      const authResult = await Promise.race([
        authPromise,
        new Promise((_, reject) => setTimeout(() => reject(new Error('Authentication timeout')), 3000))
      ]);
      
      if (!authResult) {
        throw new Error('Authentication failed');
      }
    } catch (authError) {
      console.error('⚠️ Authentication error:', authError.message);
      if (customBaseUrl) this.currentBaseUrl = originalBaseUrl;
      throw new Error('Authentication failed. Please check your credentials.');
    }

    // Normalize path
    if (!webdavPath) {
      webdavPath = `/home/${this.username}/`;
    }
    if (!webdavPath.endsWith('/')) {
      webdavPath += '/';
    }

    console.log(`📂 Fetching directory: ${webdavPath} (Force: ${forceRefresh})`);
    
    try {
      // Create abort controller for request timeout
      const controller = new AbortController();
      const timeoutId = setTimeout(() => {
        controller.abort();
      }, 15000); // 15 second timeout

      const response = await axios({
        method: 'PROPFIND',
        url: `${this.getBaseUrl()}${webdavPath}`,
        headers: {
          'Authorization': this.getAuthHeader(),
          'Depth': '1',
          'Content-Type': 'application/xml',
        },
        timeout: 15000,
        signal: controller.signal,
        // Add response size limit to prevent memory issues
        maxContentLength: 50 * 1024 * 1024, // 50MB max
        maxBodyLength: 50 * 1024 * 1024
      });
      clearTimeout(timeoutId);

      // Quick validation of response
      if (!response.data || typeof response.data !== 'string') {
        throw new Error('Invalid response format from WebDAV server');
      }

      // Parse XML with error handling
      let xmlDoc;
      try {
        const parser = new DOMParser();
        xmlDoc = parser.parseFromString(response.data, "text/xml");
        
        // Check for XML parsing errors
        const parserError = xmlDoc.getElementsByTagName("parsererror");
        if (parserError.length > 0) {
          throw new Error('XML parsing failed: ' + parserError[0].textContent);
        }
      } catch (parseError) {
        console.error('❌ XML parsing error:', parseError);
        throw new Error('Failed to parse server response');
      }

      const responses = xmlDoc.getElementsByTagNameNS("DAV:", "response");
      let items = [];
      
      // Process items with batching for large directories
      const batchSize = 100;
      const totalItems = responses.length;
      
      // Skip first item (current directory) and process in batches
      for (let batchStart = 1; batchStart < totalItems; batchStart += batchSize) {
        const batchEnd = Math.min(batchStart + batchSize, totalItems);
        
        // Process current batch
        for (let i = batchStart; i < batchEnd; i++) {
          try {
            const response = responses[i];
            const hrefElement = response.getElementsByTagNameNS("DAV:", "href")[0];
            const propsElement = response.getElementsByTagNameNS("DAV:", "prop")[0];
            
            if (!hrefElement || !propsElement) {
              continue; // Skip malformed entries
            }
            
            const href = hrefElement.textContent;
            
            // Check if it's a directory
            const resourceTypeNode = propsElement.getElementsByTagNameNS("DAV:", "resourcetype")[0];
            const isDirectory = resourceTypeNode ? 
              resourceTypeNode.getElementsByTagNameNS("DAV:", "collection").length > 0 : false;
            
            // Get file size (only for files)
            let size = 0;
            if (!isDirectory) {
              const contentLengthNode = propsElement.getElementsByTagNameNS("DAV:", "getcontentlength")[0];
              if (contentLengthNode && contentLengthNode.textContent) {
                size = parseInt(contentLengthNode.textContent, 10) || 0;
              }
            }
            
            // Get last modified date
            let modified = '';
            const lastModifiedNode = propsElement.getElementsByTagNameNS("DAV:", "getlastmodified")[0];
            if (lastModifiedNode && lastModifiedNode.textContent) {
              try {
                modified = new Date(lastModifiedNode.textContent).toISOString();
              } catch (dateError) {
                // Keep empty if date parsing fails
              }
            }
            
            // Extract name from href with proper decoding
            let name = '';
            try {
              const pathParts = href.split('/').filter(Boolean);
              name = pathParts.length > 0 ? decodeURIComponent(pathParts.pop()) : '';
            } catch (decodeError) {
              // Fallback to raw name if decoding fails
              const pathParts = href.split('/').filter(Boolean);
              name = pathParts.length > 0 ? pathParts.pop() : '';
            }
            
            // Only add items with valid names
            if (name && name.trim() !== '') {
              items.push({ 
                name: name.trim(), 
                path: href, 
                isDirectory, 
                size, 
                modified 
              });
            }
            
          } catch (itemError) {
            console.warn('⚠️ Error processing directory item:', itemError.message);
            // Continue processing other items
          }
        }
        
        // Add small delay between batches for large directories to prevent blocking
        if (batchEnd < totalItems && totalItems > 500) {
          await new Promise(resolve => setTimeout(resolve, 1));
        }
      }
      
      // Sort items for consistent ordering (directories first, then alphabetically)
      items.sort((a, b) => {
        if (a.isDirectory && !b.isDirectory) return -1;
        if (!a.isDirectory && b.isDirectory) return 1;
        return a.name.toLowerCase().localeCompare(b.name.toLowerCase());
      });
      
      const listingData = { 
        path: webdavPath, 
        items: items,
        itemCount: items.length,
        fetchTime: new Date().toISOString()
      };
      
      // Cache the result with size-aware eviction
      this.cacheDirectoryListing(cacheKey, listingData);
      
      console.log(`✅ Directory listing complete: ${items.length} items from ${webdavPath}`);
      return listingData;

    } catch (error) {
      console.error(`❌ Error fetching directory ${webdavPath}:`, error.message);
      
      // Enhanced error handling with specific messages
      if (error.code === 'ECONNABORTED' || error.message.includes('timeout')) {
        throw new Error('Request timed out. The server may be slow or the directory contains too many files.');
      } else if (error.response) {
        const status = error.response.status;
        switch (status) {
          case 401:
            throw new Error('Authentication failed. Please check your credentials.');
          case 403:
            throw new Error('Access denied. You may not have permission to access this folder.');
          case 404:
            throw new Error('Folder not found or has been moved.');
          case 500:
            throw new Error('Server error. Please try again later.');
          case 507:
            throw new Error('Server storage full or temporarily unavailable.');
          default:
            throw new Error(`Server error (${status}). Please try again.`);
        }
      } else if (error.message.includes('abort')) {
        throw new Error('Request was cancelled due to timeout.');
      }
      
      throw new Error(`Connection failed: ${error.message}`);
      
  } finally {
      // Always reset base URL
      if (customBaseUrl) {
        this.currentBaseUrl = originalBaseUrl;
      }
  }
}

// Helper method for intelligent cache management
cacheDirectoryListing(cacheKey, listingData) {
    // Check cache size and evict old entries if needed
    if (this.listingCache.size > 100) {
        const entries = Array.from(this.listingCache.entries());
        // Sort by timestamp and remove oldest 20%
        entries.sort((a, b) => a[1].timestamp - b[1].timestamp);
        const toRemove = Math.floor(entries.length * 0.2);
        
        for (let i = 0; i < toRemove; i++) {
            this.listingCache.delete(entries[i][0]);
        }
        
        console.log(`🧹 Cache cleanup: Removed ${toRemove} old entries`);
    }
    
    // Add new entry
    this.listingCache.set(cacheKey, { 
        timestamp: Date.now(), 
        data: listingData 
    });
}
  
  // Create a directory - Improved with error handling
  async createDirectory(path, customBaseUrl = null) {
    try {
      // If a custom base URL is provided for this call, temporarily use it
      if (customBaseUrl) {
        this.setCustomBaseUrl(customBaseUrl);
      }
      
      // Ensure we're authenticated
      await this.ensureAuthenticated();
      
      // First check if directory already exists
      try {
        const checkResponse = await axios({
          method: 'PROPFIND',
          url: `${this.getBaseUrl()}${path}`,
          headers: {
            'Authorization': this.getAuthHeader(),
            'Depth': '0',
            'Content-Type': 'application/xml',
          },
          timeout: 5000,
        });
        
        // If we get here, the directory exists
        return { success: true, message: 'Folder already exists' };
      } catch (checkError) {
        // 404 means directory doesn't exist, which is what we want
        if (checkError.response && checkError.response.status !== 404) {
          throw checkError; // Another error occurred
        }
      }
      
      // Create the directory
      await axios({
        method: 'MKCOL',
        url: `${this.getBaseUrl()}${path}`,
        headers: {
          'Authorization': this.getAuthHeader(),
          'Content-Type': 'application/xml',
        },
        timeout: 5000,
      });
      
      return { success: true, message: 'Folder created successfully' };
    } catch (error) {
      console.error('Error creating directory:', error);
      
      // Handle specific error cases
      if (error.response) {
        if (error.response.status === 405) {
          return { 
            success: false, 
            message: 'A folder with this name already exists' 
          };
        } else if (error.response.status === 401) {
          throw new Error('Authentication failed. Please check your credentials.');
        } else if (error.response.status === 403) {
          throw new Error('Permission denied. You may not have write access to this location.');
        }
      }
      
      throw error;
    }
}
  
// Upload a file with custom base URL support and progress tracking
async uploadFile(path, file, onProgress, customBaseUrl = null) {
  // If a custom base URL is provided, use it
  if (customBaseUrl) {
    this.setCustomBaseUrl(customBaseUrl);
  }
  
  // Make sure we're authenticated first
  await this.ensureAuthenticated();
  
  // These variables must be declared here to be in scope for the entire function
  let simulatedProgressInterval = null;
  let simulatedProgress = 0;
  
  try {
    console.log(`📤 Starting upload: ${file.name} (${file.size} bytes) to ${path}`);
    
    // Start a simulated progress timer to show movement on the UI
    if (onProgress) {
      simulatedProgressInterval = setInterval(() => {
        if (simulatedProgress < 99) {
          simulatedProgress += 1;
          onProgress(simulatedProgress);
        }
      }, 500); // Update every 0.5 seconds
    }
    
    const response = await axios({
      method: 'PUT',
      url: `${this.getBaseUrl()}${path}`,
      headers: {
        'Authorization': this.getAuthHeader(),
        'Content-Type': file.type || 'application/octet-stream',
      },
      data: file,
      onUploadProgress: progressEvent => {
        if (progressEvent.lengthComputable) {
          const percentCompleted = Math.round(
            (progressEvent.loaded * 100) / progressEvent.total
          );
          // Stop the simulated progress and use the real one
          if (simulatedProgressInterval) {
            clearInterval(simulatedProgressInterval);
            simulatedProgressInterval = null; // Clear the variable after stopping the timer
          }
          if (onProgress) {
            onProgress(percentCompleted);
          }
        }
      },
      timeout: 1200000,
      maxContentLength: 500 * 1024 * 1024,
      maxBodyLength: 500 * 1024 * 1024
    });
    
    // Clear the interval if the upload completes successfully
    if (simulatedProgressInterval) {
      clearInterval(simulatedProgressInterval);
    }
    
    console.log(`✅ Upload completed: ${file.name}`);
    return true;
  } catch (error) {
    // Clear the interval if an error occurs
    if (simulatedProgressInterval) {
      clearInterval(simulatedProgressInterval);
    }
    console.error(`❌ Error uploading file ${file.name}:`, error);
    
    // Check if it's a "NOT_ALLOWED_FOLDER" error
    if (error.response && error.response.data) {
      const errorText = typeof error.response.data === 'string' 
        ? error.response.data 
        : JSON.stringify(error.response.data);
      
      if (errorText.includes('NOT_ALLOWED_FOLDER')) {
        const match = errorText.match(/NOT_ALLOWED_FOLDER:\s*(.+)/);
        const message = match ? match[1].trim() : 'Operation not allowed outside device folders.';
        const customError = new Error(message);
        customError.code = 'NOT_ALLOWED_FOLDER';
        throw customError;
      }
    }
    
    throw error;
  }
}
  
// Download a file with custom base URL support
async downloadFile(path, customBaseUrl = null, options = {}) {
  // If a custom base URL is provided, use it
  if (customBaseUrl) {
    this.setCustomBaseUrl(customBaseUrl);
  }
  
  // Make sure we're authenticated first
  await this.ensureAuthenticated();
  
  try {
    const response = await axios({
      method: 'GET',
      url: `${this.getBaseUrl()}${path}`,
      headers: {
        'Authorization': this.getAuthHeader(),
        // Add Range header if specified in options
        ...(options.range && { Range: options.range }),
      },
      responseType: 'blob',
      timeout: options.timeout || 1200000, // 20 minutes
      maxContentLength: Infinity,
      maxBodyLength: Infinity,
      onDownloadProgress: (progressEvent) => {
        if (options.onProgress && progressEvent.lengthComputable) {
          const percentCompleted = Math.round(
            (progressEvent.loaded * 100) / progressEvent.total
          );
          options.onProgress(percentCompleted);
        }
      }
    });
    
    return response.data;
  } catch (error) {
    console.error('Error downloading file:', error);
    throw error;
  }
}
  
  // Rename/move a file or directory with custom base URL support
  async move(sourcePath, destinationPath, overwrite = true, customBaseUrl = null) {
    // If a custom base URL is provided, use it
    if (customBaseUrl) {
      this.setCustomBaseUrl(customBaseUrl);
    }
    
    // Make sure we're authenticated first
    await this.ensureAuthenticated();
    
    try {
      await axios({
        method: 'MOVE',
        url: `${this.getBaseUrl()}${sourcePath}`,
        headers: {
          'Authorization': this.getAuthHeader(),
          'Destination': `${this.getBaseUrl()}${destinationPath}`,
          'Overwrite': overwrite ? 'T' : 'F',
        },
        timeout: 10000,
      });
      
      return true;
    } catch (error) {
      console.error('Error moving file/directory:', error);
      
      // Check if it's a "NOT_ALLOWED_FOLDER" error
      if (error.response && error.response.data) {
        const errorText = typeof error.response.data === 'string' 
          ? error.response.data 
          : JSON.stringify(error.response.data);
        
        if (errorText.includes('NOT_ALLOWED_FOLDER')) {
          const match = errorText.match(/NOT_ALLOWED_FOLDER:\s*(.+)/);
          const message = match ? match[1].trim() : 'Operation not allowed outside device folders.';
          const customError = new Error(message);
          customError.code = 'NOT_ALLOWED_FOLDER';
          throw customError;
        }
      }
      
      throw error;
    }
  }
  
  // Copy a file or directory with custom base URL support
  async copy(sourcePath, destinationPath, overwrite = false, customBaseUrl = null) {
    // If a custom base URL is provided, use it
    if (customBaseUrl) {
      this.setCustomBaseUrl(customBaseUrl);
    }
    
    // Make sure we're authenticated first
    await this.ensureAuthenticated();
    
    try {
      await axios({
        method: 'COPY',
        url: `${this.getBaseUrl()}${sourcePath}`,
        headers: {
          'Authorization': this.getAuthHeader(),
          'Destination': `${this.getBaseUrl()}${destinationPath}`,
          'Overwrite': overwrite ? 'T' : 'F',
        },
        timeout: 10000,
      });
      
      return true;
    } catch (error) {
      console.error('Error copying file/directory:', error);
      throw error;
    }
  }
  
  // Delete a file or directory with custom base URL support
  async delete(path, customBaseUrl = null) {
    // If a custom base URL is provided, use it
    if (customBaseUrl) {
      this.setCustomBaseUrl(customBaseUrl);
    }
    
    // Make sure we're authenticated first
    await this.ensureAuthenticated();
    
    try {
      await axios({
        method: 'DELETE',
        url: `${this.getBaseUrl()}${path}`,
        headers: {
          'Authorization': this.getAuthHeader(),
        },
        timeout: 10000,
      });
      
      return true;
    } catch (error) {
      console.error('Error deleting file/directory:', error);
      
      // Check if it's a "NOT_ALLOWED_FOLDER" error
      if (error.response && error.response.data) {
        const errorText = typeof error.response.data === 'string' 
          ? error.response.data 
          : JSON.stringify(error.response.data);
        
        if (errorText.includes('NOT_ALLOWED_FOLDER')) {
          const match = errorText.match(/NOT_ALLOWED_FOLDER:\s*(.+)/);
          const message = match ? match[1].trim() : 'Operation not allowed outside device folders.';
          const customError = new Error(message);
          customError.code = 'NOT_ALLOWED_FOLDER';
          throw customError;
        }
      }
      
      throw error;
    }
  }
  
  // Get file preview URL with custom base URL support and auth token
  getPreviewUrl(path, customBaseUrl = null) {
    // If a custom base URL is provided, use it temporarily
    const baseUrl = customBaseUrl || this.getBaseUrl();
    
    // Generate URL with auth token (to avoid browser prompts)
    const authHeader = this.getAuthHeader();
    
    // For security, don't append credentials directly to URL
    // Instead, we'll handle auth through interceptors
    return `${baseUrl}${path}`;
  }
  
  // Get file type from extension
  getFileType(filename) {
    const extension = filename.split('.').pop().toLowerCase();
    
    // Image types
    if (['jpg', 'jpeg', 'png', 'gif', 'bmp', 'svg', 'webp'].includes(extension)) {
      return 'image';
    }
    
    // Video types
    if (['mp4', 'webm', 'avi', 'mov', 'wmv', 'mkv', 'flv'].includes(extension)) {
      return 'video';
    }
    
    // Audio types
    if (['mp3', 'wav', 'ogg', 'flac', 'aac'].includes(extension)) {
      return 'audio';
    }
    
    // Document types
    if (['pdf', 'doc', 'docx', 'xls', 'xlsx', 'ppt', 'pptx', 'txt', 'rtf'].includes(extension)) {
      return 'document';
    }
    
    // Default
    return 'file';
  }
  
  // Helper method to fetch WebDAV credentials - exposed for external use
  async fetchWebDAVCredentials() {
    return await this.fetchCredentials();
  }
  
  
  // Fetch file as blob (used for preview)
  async fetchBlob(path, customBaseUrl = null) {
    // This function essentially does the same as downloadFile
    return await this.downloadFile(path, customBaseUrl);
  }

  // Clear all credentials - useful when logging out or switching users
clearCredentials() {
  this.username = '';
  this.password = '';
  this.authInitialized = false;
  this.lastAuthenticatedUser = null;
  localStorage.removeItem('webdavPassword');
  localStorage.removeItem('webdavCredentials');
  this.clearThumbnailCache(); // Add this line
  this.clearListingCache(); // Add this line
  console.log('WebDAV credentials cleared');
}

// Method to clear the directory listing cache
clearListingCache() {
    this.listingCache.clear();
    console.log('WebDAV directory listing cache cleared.');
}
}

// Create and export a singleton instance
const webdavService = new WebDAVService();

// Set up a global axios interceptor to handle WebDAV authentication
axios.interceptors.request.use(config => {

// Only intercept WebDAV requests to our domain

if (config.url) {
  const hosts = getConfiguredHostnames();
  let urlHost = '';
  try {
    urlHost = new URL(config.url, BASE_URL).hostname;
  } catch {}

  if (
    config.url.includes('/home/') ||
    (urlHost && hosts.has(urlHost))
  ) {

    // Add authorization header if not already present
    if (!config.headers.Authorization) {
      const authHeader = webdavService.getAuthHeader();
      if (authHeader) config.headers.Authorization = authHeader;
    }
  }
}

return config;
}, error => {

return Promise.reject(error);
});

// Create axios instance specifically for WebDAV with auth interceptor
const webdavAxios = axios.create();

// Set up auth interceptor for all webdav requests
webdavAxios.interceptors.request.use(config => {

// Always add auth header to webdav requests

const authHeader = webdavService.getAuthHeader();

if (authHeader) {

    config.headers.Authorization = authHeader;

}

return config;
}, error => {

return Promise.reject(error);
});

webdavService.getCredentialsForUrl = function(url) {
  // No-op, do not embed credentials in URL
  return url;
};

// Expose method to get pre-authenticated URL
webdavService.getAuthenticatedUrl = function(url) {
  // No-op, just return the original URL
  return url;
};

export default webdavService;
